import { useState, useEffect } from "react";
import { X, Save, AlertTriangle, Link } from "lucide-react";
import { noeudsAPI } from "../../api/services";
import { TYPES_CAPTEUR, TYPES_ACTIONNEUR } from "../../data/noeudTypes";

const categories = ["Capteur", "Actionneur", "Passerelle"];
const typesByCategorie = {
  Capteur:    TYPES_CAPTEUR,
  Actionneur: TYPES_ACTIONNEUR,
  Passerelle: ["Gateway LoRa", "Gateway WiFi", "Gateway 4G"],
};

export default function EditNoeudModal({ isOpen, onClose, onSave, noeud }) {
  const [categorie, setCategorie]       = useState("");
  const [type, setType]                 = useState("");
  const [deviceId, setDeviceId]         = useState("");
  const [mac, setMac]                   = useState("");
  const [localisation, setLocalisation] = useState("");
  const [fiche, setFiche]               = useState("");
  const [deps, setDeps]                 = useState([]);
  const [loadingDeps, setLoadingDeps]   = useState(false);

  // Calibration
  const [calMode, setCalMode]                   = useState("none");
  const [calMinRaw, setCalMinRaw]               = useState("");
  const [calMaxRaw, setCalMaxRaw]               = useState("");
  const [calMinPhysique, setCalMinPhysique]     = useState("");
  const [calMaxPhysique, setCalMaxPhysique]     = useState("");
  const [calUnitePhysique, setCalUnitePhysique] = useState("");

  useEffect(() => {
    if (noeud) {
      setCategorie(noeud.categorie || "");
      setType(noeud.type || "");
      setDeviceId(noeud.deviceId || "");
      setMac(noeud.mac || "");
      setLocalisation(noeud.localisation || "");
      setFiche(noeud.fiche || "");

      const cal = noeud.calibration || {};
      setCalMode(cal.mode || "none");
      setCalMinRaw(cal.minRaw ?? "");
      setCalMaxRaw(cal.maxRaw ?? "");
      setCalMinPhysique(cal.minPhysique ?? "");
      setCalMaxPhysique(cal.maxPhysique ?? "");
      setCalUnitePhysique(cal.unitePhysique || "");

      // Charger les dépendances
      setLoadingDeps(true);
      noeudsAPI.getDependencies(noeud._id)
        .then(res => setDeps(res.data?.data || []))
        .catch(() => setDeps([]))
        .finally(() => setLoadingDeps(false));
    }
  }, [noeud]);

  if (!isOpen) return null;

  const hasDeps = deps.length > 0;
  const types = typesByCategorie[categorie] || [];
  const inputStyle = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const lockedStyle = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-100 text-slate-500 cursor-not-allowed";

  const handleSave = () => {
    let calibration = { mode: "none" };
    if (categorie === "Capteur" && calMode !== "none") {
      calibration = {
        mode: calMode,
        minRaw: calMinRaw === "" ? null : parseFloat(calMinRaw),
        maxRaw: calMaxRaw === "" ? null : parseFloat(calMaxRaw),
        minPhysique: calMinPhysique === "" ? null : parseFloat(calMinPhysique),
        maxPhysique: calMaxPhysique === "" ? null : parseFloat(calMaxPhysique),
        unitePhysique: calUnitePhysique.trim() || null
      };
    }
    onSave({
      ...noeud,
      categorie, type, deviceId, mac, localisation, fiche,
      calibration
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 z-10 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Modifier le noeud IoT</h2>
            <p className="text-sm text-slate-400 mt-0.5">Modifiez les informations du noeud sélectionné</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-slate-400">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">

          {/* SN — lecture seule */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Numéro de série (SN)</label>
            <input
              value={noeud?.sn || ""}
              readOnly
              className="w-full px-4 py-3 border border-blue-200 rounded-xl text-sm font-mono font-semibold bg-blue-100/50 text-slate-600 cursor-not-allowed"
            />
            <p className="text-xs text-blue-500 mt-1">Le SN ne peut pas être modifié — c'est l'identifiant physique de l'appareil</p>
          </div>

          {/* Bannière dépendances */}
          {loadingDeps ? (
            <div className="text-xs text-slate-400 py-2">Vérification des dépendances...</div>
          ) : hasDeps && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Noeud utilisé dans {deps.length} élément(s)
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    La catégorie et le type sont verrouillés. Supprimez d'abord ces dépendances pour les modifier.
                  </p>
                  <div className="mt-2 space-y-1">
                    {deps.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                        <Link size={10} /> <span className="font-medium">{d.type}</span> : {d.nom}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Type d'appareil */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-4">Type d'appareil</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Catégorie <span className="text-red-400">*</span>
                  {hasDeps && <span className="text-xs text-amber-500 ml-2">verrouillé</span>}
                </label>
                {hasDeps ? (
                  <input value={categorie} readOnly className={lockedStyle} />
                ) : (
                  <select
                    value={categorie}
                    onChange={e => { setCategorie(e.target.value); setType(""); }}
                    className={inputStyle}
                  >
                    <option value="">Sélectionner</option>
                    {categories.map(c => <option key={c}>{c}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Type <span className="text-red-400">*</span>
                  {hasDeps && <span className="text-xs text-amber-500 ml-2">verrouillé</span>}
                </label>
                {hasDeps ? (
                  <input value={type} readOnly className={lockedStyle} />
                ) : (
                  <select
                    value={type}
                    onChange={e => setType(e.target.value)}
                    disabled={!categorie}
                    className={`${inputStyle} disabled:opacity-40`}
                  >
                    <option value="">Sélectionner</option>
                    {types.map(t => <option key={t}>{t}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Calibration (capteurs uniquement) */}
          {categorie === "Capteur" && (
            <div className="border border-gray-200 rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-1">Calibration du signal</h3>
              <p className="text-xs text-slate-500 mb-3">Si le device envoie un signal brut (mA, V), Sagrada le convertit en valeur physique. Sinon laisse "Aucune".</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mode</label>
                  <select value={calMode} onChange={e => {
                    const m = e.target.value;
                    setCalMode(m);
                    if (m === "4-20mA") { setCalMinRaw("4"); setCalMaxRaw("20"); }
                    else if (m === "0-10V") { setCalMinRaw("0"); setCalMaxRaw("10"); }
                    else if (m === "none") { setCalMinRaw(""); setCalMaxRaw(""); }
                  }} className={inputStyle}>
                    <option value="none">Aucune (valeur reçue utilisée telle quelle)</option>
                    <option value="4-20mA">4–20 mA (boucle de courant)</option>
                    <option value="0-10V">0–10 V (tension analogique)</option>
                    <option value="linear">Linéaire personnalisée</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Unité physique</label>
                  <input value={calUnitePhysique} onChange={e => setCalUnitePhysique(e.target.value)}
                    placeholder="°C, %, ppm, hPa…" className={inputStyle} disabled={calMode === "none"} />
                </div>
              </div>
              {calMode !== "none" && (
                <div className="grid grid-cols-4 gap-3 mt-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Min brut</label>
                    <input type="number" step="any" value={calMinRaw} onChange={e => setCalMinRaw(e.target.value)}
                      className={inputStyle} disabled={calMode === "4-20mA" || calMode === "0-10V"} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Max brut</label>
                    <input type="number" step="any" value={calMaxRaw} onChange={e => setCalMaxRaw(e.target.value)}
                      className={inputStyle} disabled={calMode === "4-20mA" || calMode === "0-10V"} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Min physique</label>
                    <input type="number" step="any" value={calMinPhysique} onChange={e => setCalMinPhysique(e.target.value)}
                      className={inputStyle} placeholder="0" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Max physique</label>
                    <input type="number" step="any" value={calMaxPhysique} onChange={e => setCalMaxPhysique(e.target.value)}
                      className={inputStyle} placeholder="100" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Infos complémentaires — toujours modifiables */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-4">Informations complémentaires</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse MAC</label>
                <input value={mac} onChange={e => setMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" className={inputStyle} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">ID interne (alias)</label>
                <input value={deviceId} onChange={e => setDeviceId(e.target.value)} placeholder="Ex: TEMP-001" className={inputStyle} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Localisation</label>
                <input value={localisation} onChange={e => setLocalisation(e.target.value)} placeholder="Ex: Bureau 1 - Étage 2" className={inputStyle} />
              </div>
            </div>
          </div>

          {/* Documentation */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-4">Documentation</h3>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Fiche technique (optionnel)</label>
              <textarea
                value={fiche}
                onChange={e => setFiche(e.target.value)}
                placeholder="URL de la fiche technique ou description du produit..."
                rows={2}
                className={`${inputStyle} resize-none`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
            <Save size={15} /> Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
}
